import type { FC } from "react";
import { useMemo } from "react";
import type { FieldTemplateProps } from "@rjsf/utils";
import styled from "@emotion/styled";
import { getValueFromUiSchema } from "@/utils/schema";
import { useTranslation } from "react-i18next";

export interface KioUiSchemaProps {
  colspan?: number;
  rowspan?: number;
  colOffset?: number;
  rowOffset?: number;
  spacing?: number;
  backgroundColor?: string;
  minWidth?: string;
  minHeight?: string;
}

const Container = styled.div<KioUiSchemaProps>`
  grid-column-start: ${({ colOffset }) => (colOffset ? colOffset : "auto")};
  grid-column-end: ${({ colspan }) => `span ${colspan || 12}`};

  grid-row-start: ${({ rowOffset }) => (rowOffset ? rowOffset + 1 : "auto")};
  grid-row-end: ${({ rowspan }) => `span ${rowspan || 1}`};

  background-color: ${({ backgroundColor }) => `${backgroundColor ? backgroundColor : "initial"}`};
  min-height: ${({ minHeight }) => `${minHeight ? minHeight : "initial"}`};
  min-width: ${({ minWidth }) => `${minWidth ? minWidth : "initial"}`};
  width: 100%;
`;

const FeatureDisabledMessage = styled("div")`
  border-radius: 4px;
  padding: 16px;
  background: #0000000d;
`;

const FieldTemplate: FC<FieldTemplateProps> = ({ hidden, uiSchema, children, onChange, formContext }) => {
  const { t } = useTranslation("common");

  // TODO: find better solution than this to manipulate formData from ArrayItems
  if (uiSchema?.items) {
    const tempItems = children.props?.children?.[0]?.props?.uiSchema?.items;
    if (tempItems)
      children.props.children[0].props.uiSchema.items["ui:options"] = {
        ...tempItems["ui:options"],
        onChange,
      };
  }
  const uiSchemaProps = useMemo<KioUiSchemaProps>(
    () => ({
      colspan: getValueFromUiSchema("colspan", uiSchema),
      rowspan: getValueFromUiSchema("rowspan", uiSchema),
      colOffset: getValueFromUiSchema("colOffset", uiSchema),
      rowOffset: getValueFromUiSchema("rowOffset", uiSchema),
      spacing: getValueFromUiSchema("spacing", uiSchema),
      backgroundColor: getValueFromUiSchema("backgroundColor", uiSchema),
      minWidth: getValueFromUiSchema("minWidth", uiSchema),
      minHeight: getValueFromUiSchema("minHeight", uiSchema),
    }),
    [uiSchema]
  );
  if (hidden) return null;

  const foundFeatureToggle = getValueFromUiSchema("featureToggle", uiSchema);
  // If feature is not allowed, render disabled feature msg if configured, else render nothing
  if (!!foundFeatureToggle && !formContext.allowedFeatures.includes(foundFeatureToggle)) {
    if (!!getValueFromUiSchema("showDisabledFeature", uiSchema)) {
      const customMessage = getValueFromUiSchema("disabledFeatureMessage", uiSchema);

      return (
        <Container {...uiSchemaProps}>
          <FeatureDisabledMessage>
            {!!customMessage ? (
              <>{customMessage}</>
            ) : (
              <>
                {t("kioForm.fields.FeatureToggleWrapperField.featureNotEnabled", { featureName: foundFeatureToggle })}
              </>
            )}
          </FeatureDisabledMessage>
        </Container>
      );
    } else {
      return null;
    }
  }

  return <Container {...uiSchemaProps}>{children}</Container>;
};

export { FieldTemplate };
